import { ref } from 'vue'
import { useContractDraft, resolveContractDraft, mapDraftError } from './useContractDraft'
import { useAuth } from './useAuth'
import { updateProspectingOptIn } from '@/services/account'
import {
  createCheckoutSession,
  type CheckoutMode,
  type ContractDraft,
  type SubscriberInfo,
} from '@/services/subscription'

/**
 * Finalization actions of the subscription recap screen. The recap collects the
 * subscriber's details and the chosen bundle(s); from there the user can pay now
 * (Stripe Checkout) or share a payment link so a third party pays.
 *
 * Both actions target the SAME `contract_draft` — created once per configuration
 * and reused (see `resolveContractDraft`) so the backend keeps one link/session
 * set per draft (idempotency + anti-double-charge). This composable owns the
 * recap's single `submitting`/`error` surface and delegates draft
 * building/caching to `useContractDraft`; the share-link page lifecycle lives in
 * `usePaymentLink`.
 */
export function useSubscriptionFinalize() {
  const { buildDraftData } = useContractDraft()
  const { isAuthenticated } = useAuth()

  /** Any finalize action in progress — disables both recap buttons. */
  const submitting = ref(false)
  /** Payer specifically: true while paying / redirecting to Stripe, so only the
   *  Payer button shows its "Redirecting…" label (Partager must not flip it). */
  const redirecting = ref(false)
  /** i18n key of the current finalization error (draft or checkout), or null. */
  const error = ref<string | null>(null)

  /**
   * Persist the commercial prospecting consent of a CONNECTED subscriber
   * (`PATCH /users`). A guest carries it inside the contract draft instead —
   * the backend rejects the field on a connected draft user.
   *
   * Deliberately non-blocking: the consent is not part of the sale, so a failed
   * call must never stop a payment the user is about to make (they can still
   * give or withdraw it from their account). Hence the swallowed error.
   */
  async function syncProspectingConsent(info: SubscriberInfo): Promise<void> {
    if (!isAuthenticated.value) return
    try {
      await updateProspectingOptIn(info.prospectingOptIn)
    } catch {
      /* consent not saved — never blocks the finalization */
    }
  }

  /**
   * Build and create-or-reuse the contract draft for the current journey.
   * Returns the draft, or null when there is nothing to subscribe to (no flow /
   * bundle not resolved) or the draft creation failed — `error` is set in both
   * cases so the caller can simply bail.
   */
  async function ensureDraft(info: SubscriberInfo): Promise<ContractDraft | null> {
    const data = buildDraftData(info)
    if (!data) {
      // No flow, or bundle/period not resolved yet — nothing to subscribe to.
      error.value = 'subscription.info.payment.error'
      return null
    }
    try {
      return await resolveContractDraft(data)
    } catch (e) {
      error.value = mapDraftError(e)
      return null
    }
  }

  /**
   * Payer: ensure the draft, open a Stripe Checkout session and hand the browser
   * over to it. `submitting` stays true through the redirect so the button
   * remains disabled.
   */
  async function pay(info: SubscriberInfo, mode: CheckoutMode): Promise<void> {
    if (submitting.value) return
    submitting.value = true
    redirecting.value = true
    error.value = null

    await syncProspectingConsent(info)
    const draft = await ensureDraft(info)
    if (!draft) {
      submitting.value = false
      redirecting.value = false
      return
    }

    try {
      const session = await createCheckoutSession(draft.id, mode)
      window.location.assign(session.url)
    } catch {
      error.value = 'subscription.info.payment.checkoutError'
      submitting.value = false
      redirecting.value = false
    }
  }

  /**
   * Partager: ensure the draft and return its id for the share-link page. Returns
   * null on failure (nothing staged; `error` already set for display).
   */
  async function shareLink(info: SubscriberInfo): Promise<string | null> {
    if (submitting.value) return null
    submitting.value = true
    error.value = null

    await syncProspectingConsent(info)
    const draft = await ensureDraft(info)
    submitting.value = false
    return draft?.id ?? null
  }

  return { pay, shareLink, submitting, redirecting, error }
}
